import { Building2 } from 'lucide-react'

import { cn } from 'lib/utils'

import { EmptyBlock } from '../EmptyBlock'
import { asArray, asNumber, asText } from '../payload'

/**
 * Renderers for the portfolio elements (MYD-501). Payload shapes mirror
 * backend/presentations/providers/portfolioElements.js. Key figures reuse
 * the generic figure tiles, whose payload shape they share.
 */

/** Up to four photos tiled behind the cover's title; one fills the frame. */
const MOSAIC: Record<number, string> = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-2 grid-rows-2' }

export function PortfolioCoverBlock({ payload }: { payload: any }) {
	const image = asText(payload?.image)
	const mosaic = asArray<string>(payload?.mosaic).map(asText).filter(Boolean).slice(0, 4)
	const hasPicture = Boolean(image) || mosaic.length > 0

	return (
		<div className={cn('relative h-full min-h-[240px] overflow-hidden', hasPicture ? 'bg-gray-900' : 'bg-gradient-to-br from-primary to-blue-900')}>
			{image ? (
				<img src={image} alt="" className="absolute inset-0 h-full w-full object-cover" draggable={false} />
			) : (
				mosaic.length > 0 && (
					<div className={cn('absolute inset-0 grid gap-0.5', MOSAIC[mosaic.length])}>
						{mosaic.map((src, index) => (
							<img key={index} src={src} alt="" className="h-full w-full object-cover" draggable={false} />
						))}
					</div>
				)
			)}
			<div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
			<div className="absolute inset-x-0 bottom-0 p-10 text-white">
				{asText(payload?.type) && <p className="mb-2 text-[18px] font-medium uppercase tracking-wider text-white/80">{asText(payload.type)} portfolio</p>}
				<h2 className="text-[56px] font-bold leading-tight">{asText(payload?.name)}</h2>
				{asText(payload?.summary) && <p className="mt-2 text-[24px] text-white/90">{asText(payload.summary)}</p>}
			</div>
		</div>
	)
}

export function PortfolioLocationsTableBlock({ payload }: { payload: any }) {
	const rows = asArray<any>(payload?.rows)
	const more = asNumber(payload?.more) ?? 0
	if (rows.length === 0) return <EmptyBlock>This portfolio has no locations yet.</EmptyBlock>

	return (
		<div>
			<table className="w-full table-fixed text-left">
				<thead>
					<tr className="border-b-2 border-gray-200 text-[16px] uppercase tracking-wide text-gray-500">
						<th className="w-[34%] py-2 pr-4 font-semibold">Location</th>
						<th className="py-2 pr-4 font-semibold">Place</th>
						<th className="py-2 pr-4 text-right font-semibold">Area</th>
						<th className="w-[16%] py-2 text-right font-semibold">Projects</th>
					</tr>
				</thead>
				<tbody>
					{rows.map((row, index) => (
						<tr key={index} className="border-b border-gray-100 text-[20px]">
							<td className="truncate py-2.5 pr-4 font-medium text-gray-900">{asText(row?.name)}</td>
							<td className="truncate py-2.5 pr-4 text-gray-600">{asText(row?.place)}</td>
							<td className="py-2.5 pr-4 text-right tabular-nums text-gray-700">{asText(row?.area) || '—'}</td>
							<td className="py-2.5 text-right tabular-nums text-gray-700">{asNumber(row?.projects) ?? 0}</td>
						</tr>
					))}
				</tbody>
			</table>
			{more > 0 && <p className="mt-3 text-[16px] text-gray-500">and {more} more</p>}
		</div>
	)
}

/** Locations per state as bars, the longest the most. */
export function PortfolioGeoBlock({ payload }: { payload: any }) {
	const rows = asArray<any>(payload?.rows)
	const more = asNumber(payload?.more) ?? 0
	if (rows.length === 0) return <EmptyBlock>This portfolio has no locations yet.</EmptyBlock>

	return (
		<div>
			<ul className="space-y-3">
				{rows.map((row, index) => {
					const share = Math.min(1, Math.max(0, asNumber(row?.share) ?? 0))
					return (
						<li key={index} className="grid grid-cols-[120px_1fr_auto] items-center gap-4">
							<span className="truncate text-[20px] font-medium text-gray-900">{asText(row?.state)}</span>
							<span className="h-7 overflow-hidden rounded-md bg-gray-100">
								<span className="block h-full rounded-md bg-primary" style={{ width: `${Math.max(2, share * 100)}%` }} />
							</span>
							<span className="text-right text-[18px] tabular-nums text-gray-700">
								<span className="font-semibold text-gray-900">{asNumber(row?.count) ?? 0}</span>
								{asText(row?.area) && <span className="ml-2 text-[15px] text-gray-500">{asText(row.area)}</span>}
							</span>
						</li>
					)
				})}
			</ul>
			{more > 0 && <p className="mt-3 text-[16px] text-gray-500">and {more} more {more === 1 ? 'state' : 'states'}</p>}
		</div>
	)
}

export function PortfolioTopLocationsBlock({ payload }: { payload: any }) {
	const entries = asArray<any>(payload?.entries)
	if (entries.length === 0) {
		return <EmptyBlock>{payload?.by === 'projects' ? 'No location has an active project.' : 'No location has a recorded size.'}</EmptyBlock>
	}
	return (
		<ol className="space-y-3">
			{entries.map((entry, index) => (
				<li key={index} className={cn('flex items-center gap-4 rounded-xl px-4 py-3', index === 0 ? 'border border-primary/20 bg-primary/5' : 'bg-gray-50')}>
					<span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[18px] font-semibold', index === 0 ? 'bg-primary text-white' : 'bg-white text-gray-600')}>
						{asNumber(entry?.rank) ?? index + 1}
					</span>
					<span className="flex h-14 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md bg-gray-200">
						{asText(entry?.image) ? <img src={asText(entry.image)} alt="" className="h-full w-full object-cover" draggable={false} /> : <Building2 className="h-6 w-6 text-gray-400" />}
					</span>
					<div className="min-w-0 flex-1">
						<p className="truncate text-[21px] font-medium text-gray-900">{asText(entry?.name)}</p>
						<p className="truncate text-[16px] text-gray-500">{asText(entry?.place)}</p>
					</div>
					<span className="shrink-0 text-[20px] font-semibold tabular-nums text-gray-900">{asText(entry?.value)}</span>
				</li>
			))}
		</ol>
	)
}

/**
 * Columns for up to twelve tiles, chosen so rows fill evenly: four tiles are
 * two by two, not three and a straggler.
 */
const COLUMNS = (count: number) => (count <= 2 || count === 4 ? 'grid-cols-2' : count === 3 || count === 5 || count === 6 || count === 9 ? 'grid-cols-3' : 'grid-cols-4')

export function PortfolioMosaicBlock({ payload }: { payload: any }) {
	const tiles = asArray<any>(payload?.tiles).filter(tile => asText(tile?.url)).slice(0, 12)
	if (tiles.length === 0) return <EmptyBlock fill>No photos to show yet.</EmptyBlock>

	return (
		<div className={cn('grid h-full min-h-0 auto-rows-fr gap-2', COLUMNS(tiles.length))}>
			{tiles.map((tile, index) => (
				<figure key={index} className="relative min-h-0 overflow-hidden rounded-lg bg-gray-100">
					<img src={asText(tile.url)} alt={asText(tile?.caption)} className="h-full w-full object-cover" draggable={false} />
					{asText(tile?.caption) && (
						<figcaption className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/70 to-transparent px-3 pb-2 pt-6 text-[15px] text-white">{asText(tile.caption)}</figcaption>
					)}
				</figure>
			))}
		</div>
	)
}
